<script lang="ts" setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router/composables'
import { v4 as uuid } from 'uuid'
import ModuleCard from './ModuleCard.vue'
import { useElementClick } from '@/composables/tracks'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { theme as brandTheme } from '@/config/brands'

interface Props {
  module: any
  config?: any
  cardClass?: string
  headerClass?: string
  titleClass?: string
  actionClass?: string
}

const props = defineProps<Props>()

const { elementClick } = useElementClick()
const router = useRouter()

/** 设计稿：Most Popular 竖排展示 3 本，其余走 More 进榜单页（仅展示层截断，不改接口数据） */
const RANK_LIMIT = 3

const books = computed(() => {
  const records: any[] = props.module?.resourceList?.records || []
  return records.slice(0, RANK_LIMIT)
})

const rankColors = computed<readonly string[]>(() =>
  props.config?.theme?.rankingNumberColors || [
    brandTheme.primaryColor || '#F0509B',
    '#7C4DE8',
    brandTheme.textTertiary || '#AB9BB8',
  ],
)

function rankColor(index: number) {
  const colors = rankColors.value
  return colors[Math.min(index, colors.length - 1)]
}

/** genre chip：purple-soft 底 + 紫字 10px（与 feed 的 pink chip 区分） */
const genreChipStyle = {
  height: '18px',
  lineHeight: '18px',
  padding: '0 8px',
  fontSize: '10px',
  color: '#7C4DE8',
  background: '#F3EDFE',
  borderRadius: '9px',
}
const genreChipWrapStyle = { height: '18px' }

function gotoCollection() {
  elementClick(`seeall_${props.module.id}`)
  router.push({ name: 'ranking', params: { columnId: props.module.id }, query: { title: props.module.showName } })
}

function gotoRead(book: any) {
  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: book.id, chapterId: book.firstChapter?.id || '' },
  })
}
</script>

<template>
  <ModuleCard
    :title-text="module.showName"
    :action-text-value="config?.texts?.seeAll || 'More'"
    :card-class="cardClass"
    :header-class="headerClass"
    :title-class="titleClass"
    :action-class="actionClass"
    @action="gotoCollection"
  >
    <div class="mt-6px">
      <div
        v-for="(book, index) in books"
        :key="book.id"
        class="flex items-center py-9px"
        @click="gotoRead(book)"
      >
        <div
          class="nm-font-title w-22px shrink-0 text-center text-22px lh-26px font-700"
          :style="{ color: rankColor(index) }"
        >
          {{ index + 1 }}
        </div>

        <BizBookCover
          :id="uuid()"
          :src="book.contentCoverUrl || ''"
          :extra="{
            module_name: module.showName,
            module_id: module.id,
            module_type: module.columnType,
            author: book.authorInfo?.author,
            ...book,
          }"
          :height-rate="135"
          class="ml-8px w-52px shrink-0 rounded-10px"
        />

        <div class="ml-10px min-w-0 flex-1">
          <p class="text-14px lh-19px font-800 line-clamp-1 text-n-text-1">
            {{ book.contentName }}
          </p>
          <div class="mt-7px flex items-center">
            <span class="max-w-45% shrink-0 truncate text-11px lh-15px text-n-text-3">
              {{ book.authorInfo?.author }}
            </span>
            <BizShowTags
              v-if="book.tagList && book.tagList.length"
              :tags="book.tagList.slice(0, 1)"
              class="ml-8px min-w-0 flex-1"
              :wrap-style="genreChipWrapStyle"
              :item-style="genreChipStyle"
            />
          </div>
        </div>
      </div>
    </div>
  </ModuleCard>
</template>
